import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import MainLayout from "../../layouts/Layout";
import { ProjectOutlined } from "@ant-design/icons";
import { useSecureService } from "../../store/service";
import { useAuth } from "../../hooks/useAuth";
import { Button, message } from "antd";
import styles from "./Projects.module.scss";
import AddNewFolderModal from "../../modals/addNewFolderModal";
import AddNewFileModal from "../../modals/addNewFileModal";
import { useLanguage } from "../../contexts/LanguageContext";
import FileDetail from "./FileDetail";
import FooterActions from "./FooterActions";
import ApprovalModal from "../../modals/approvalModal";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import NewEntrySection from "../../components/NewEntrySection/NewEntrySection";
import ProjectItemCard from "../../components/ProjectItemCard/ProjectItemCard";
import AddNewProjectModal from "../../modals/addNewProjectModal";
import NoDataComponent from "../../components/NoDataComponent/NoDataComponent";
import HeaderlessLayout from "../../layouts/HeaderlessLayout";


const ProjectDetail = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getEntryByPath, getFileById, deleteEntry } = useSecureService();

  const [entry, setEntry] = useState<any>(null);
  const [parentEntries, setParentEntries] = useState<any[]>([]);
  const [projectId, setProjectId] = useState<number | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [isEntryPopoverOpen, setIsEntryPopoverOpen] = useState(false);
  const [isFolderModalOpen, setIsFolderModalOpen] = useState(false);
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [, setIsSelectMode] = useState(false);
  
  // Approval modal state'leri
  const [isApprovalModalVisible, setIsApprovalModalVisible] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);
  
  // Edit modal state'leri
  const [isEditFileModalVisible, setIsEditFileModalVisible] = useState(false);
  const [isEditFolderModalVisible, setIsEditFolderModalVisible] = useState(false);
  const [isEditProjectModalVisible, setIsEditProjectModalVisible] = useState(false);
  

  


  const pathParts = location.pathname.split('/').filter(p => p && p.trim() !== '');
  const fullPath = pathParts.join('/');
  const getProjectsBasePath = () => {
    const fromState = (location.state as any)?.fromProjectsBasePath;
    if (fromState === '/internal-projects' || fromState === '/projects' || fromState === '/external-projects') {
      return fromState;
    }
    const saved = localStorage.getItem('lastProjectsBasePath');
    if (saved === '/internal-projects' || saved === '/projects' || saved === '/external-projects') {
      return saved;
    }
    return '/internal-projects';
  };

  // URL'den proje adını al




  const handleRefresh = async () => {
    if (!fullPath) {
      navigate(getProjectsBasePath());
      return;
    }
    

    
    setLoading(true);
    try {
      const response = await getEntryByPath(fullPath);
      
      if (response.success && response.data) {
          const entryData = response.data;
          
          // projectId'yi set et - Entry type'a göre belirle
          if ((entryData as any).entryType === 'Project') {
            // Project ise id'si projectId olarak kullanılır
            setProjectId((entryData as any).id);
          } else {
            // Folder ise servisten gelen projectId kullanılır
            setProjectId((entryData as any).projectId);
          }

        // Eğer bu bir dosya ise ve versions bilgisi yoksa, getFileById ile tam detayını al
        if ((entryData as any).entryType === 2 || ((entryData as any).entryType && String((entryData as any).entryType) === 'File')) {
          if (entryData.id) {
            try {
              const fileResponse = await getFileById(entryData.id);
              if (fileResponse.success && fileResponse.data) {
                // entryType ve path'i koru, getFileById'den gelen data'da entryType ve path yanlış olabilir
                setEntry({ 
                  ...fileResponse.data, 
                  entryType: entryData.entryType,
                  path: entryData.path || fileResponse.data.path // EntryData'dan gelen path'i öncelikle kullan
                });
              } else {
                setEntry(entryData);
              }
            } catch (error) {
              // getFileById error for main entry
              setEntry(entryData);
            }
          } else {
            setEntry(entryData);
          }
        } else {
          // Eğer bu bir klasör/proje ise, children'lardaki dosyaları da güncelle
          if (entryData.children && Array.isArray(entryData.children)) {
            
            const updatedChildren = await Promise.all(
              entryData.children.map(async (child: any) => {
                if ((child as any).entryType === 2 || ((child as any).entryType && String((child as any).entryType) === 'File')) {
                  if (child.id) {
                    try {
                      const fileResponse = await getFileById(child.id);
                      if (fileResponse.success && fileResponse.data) {
                        // entryType ve path'i koru, getFileById'den gelen data'da entryType ve path yanlış olabilir
                        return { 
                          ...fileResponse.data, 
                          entryType: child.entryType,
                          path: child.path || fileResponse.data.path // Child'dan gelen path'i öncelikle kullan
                        };
                      }
                    } catch (error) {
                      // getFileById error for child
                    }
                  }
                }
                return child;
              })
            );
            
            setEntry({ ...entryData, children: updatedChildren });
          } else {
            setEntry(entryData);
          }
        }
      } else {
        setEntry(null);
      }
    } catch (error) {
      setEntry(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleRefresh();
  }, [location.pathname]);





  const handleNavigation = (item: any) => {
    // Path kontrolü - path yoksa veya boşsa navigate etme
    if (!item || !item.path || typeof item.path !== 'string' || item.path.trim() === '') {
      console.error('Item path is missing or invalid:', item);
      return;
    }
    
    // Path'i temizle ve normalize et
    const cleanPath = item.path.trim();
    const newPath = cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
    
    // Dosya kartında her zaman File EntryPath'e git (UUID path), versiyonda seçim FileDetail içinde yapılır
    if (item.entryType === 'File' || String(item.entryType) === '2') {
      navigate(newPath, { state: { fromProjectsBasePath: getProjectsBasePath() } });
      return;
    }
    // Klasör/Proje için path-based navigation
    navigate(newPath, { state: { fromProjectsBasePath: getProjectsBasePath() } });
  };

  const handleGoBack = () => {
    const parentPath = `/${pathParts.slice(0, -1).join('/')}`;
    if (pathParts.length > 1) {
      navigate(parentPath, { state: { fromProjectsBasePath: getProjectsBasePath() } });
    } else {
      navigate(getProjectsBasePath());
    }
  };

  // Toplu seçim yetkisi kontrolü - sadece admin, super admin ve account owner
  const canMultiSelect = () => {
    if (!user) return false;
    return ['Admin', 'SuperAdmin', 'AccountOwner'].includes(user.role);
  };

  const handleItemSelect = (itemId: number, checked: boolean) => {
    if (checked) {
      // Toplu seçim kontrolü - sadece yetkili roller birden fazla seçim yapabilir
      if (selectedItems.size > 0 && !canMultiSelect()) {
        // Yetkisiz kullanıcı zaten bir öğe seçiliyse yeni seçim yapamaz
        message.warning('You can only make single selections. You do not have permission for multi-selection.');
        return;
      }
      
      const newSelectedItems = new Set(selectedItems);
      newSelectedItems.add(itemId);
      setSelectedItems(newSelectedItems);
    } else {
      const newSelectedItems = new Set(selectedItems);
      newSelectedItems.delete(itemId);
      setSelectedItems(newSelectedItems);
    }
  };


  const handleBulkDelete = async () => {
    try {
      if (selectedItems.size > 0) {
        // Seçilen öğeleri sil
       
        
        // Seçili öğelerin detaylarını al
        const selectedItemsDetails = entry?.children
          ?.filter((item: any) => selectedItems.has(item.id))
          .map((item: any) => ({
            id: item.id,
            entryType: item.entryType
          })) || [];
        
        if (selectedItemsDetails.length === 0) {
          throw new Error('No items to delete');
        }
        
        // deleteEntry API çağrısı yap
        const response = await deleteEntry(selectedItemsDetails);
        if (!response.success) {
          throw new Error(`Failed to delete items: ${response.message}`);
        }
        
        // Başarılı silme sonrası state'i temizle ve sayfayı yenile
        setSelectedItems(new Set());
        setIsSelectMode(false);
        
        // Success mesajı göster
        message.success('Selected items deleted successfully.');
        
        // Sayfayı yenile
        await handleRefresh();
      } else {
        // Seçili öğe yoksa mevcut klasörü sil
        
        if (!entry?.id) {
          throw new Error('No entry to delete');
        }
        
        const response = await deleteEntry([{
          id: entry.id,
          entryType: entry.entryType
        }]);
        
        if (!response.success) {
          throw new Error(`Failed to delete entry: ${response.message}`);
        }
        
        // Success mesajı göster
        message.success(`${entry.name} deleted successfully.`);
        
        // Ana sayfaya yönlendir
        navigate(getProjectsBasePath());
      }
      
    } catch (error) {
      // Delete error
      throw error; // FooterActions'da handle edilecek
    }
  };

  const handleBulkDownload = async () => {
    if (selectedItems.size === 0) {
      return;
    }
    
    // Seçilen öğeleri indir
   
    
    // TODO: API çağrısı ile seçilen öğeleri indir
  };

  const handleBulkCopyUrl = async () => {
    try {
      if (selectedItems.size > 0) {
        // Seçilen öğelerin URL'lerini kopyala
       
        
        // Seçili öğelerin URL'lerini oluştur
        const urls = entry?.children
          ?.filter((item: any) => selectedItems.has(item.id))
          .map((item: any) => {
            const itemPath = `/${item.path}`;
            return `${window.location.origin}${itemPath}`;
          }) || [];
        
        if (urls.length > 0) {
          const urlText = urls.join('\n');
          await navigator.clipboard.writeText(urlText);
          message.success(`URLs of ${urls.length} items copied.`);
        }
      } else {
        // Seçili öğe yoksa mevcut sayfanın URL'sini kopyala
        const currentUrl = window.location.href;
        await navigator.clipboard.writeText(currentUrl);
                  message.success('Page URL copied.');
      }
    } catch (error) {
      // Copy URL error
              message.error('An error occurred while copying URL.');
    }
  };

  // Approval modal fonksiyonları
  const handleApprovalConfirm = async (changeSubentries: boolean) => {
    setApprovalLoading(true);
    try {
      // Modal'ı kapat
      setIsApprovalModalVisible(false);
    } catch (error) {
      message.error('Failed to change status. Please try again.');
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleApprovalCancel = () => {
    setIsApprovalModalVisible(false);
  };

  // Edit fonksiyonları
  const handleEditClick = () => {
    // Seçili öğe yoksa işlem yapma
    if (!selectedItems || selectedItems.size === 0) {
      message.error('Please select at least one item.');
      return;
    }
    
    // Seçili öğenin tipine göre ilgili modal'ı aç
    const firstSelectedItem = entry?.children?.find((item: any) => selectedItems.has(item.id));
    if (firstSelectedItem) {
      if (firstSelectedItem.entryType === 'File') {
        setIsEditFileModalVisible(true);
      } else if (firstSelectedItem.entryType === 'Folder') {
        setIsEditFolderModalVisible(true);
      } else if (firstSelectedItem.entryType === 'Project') {
        setIsEditProjectModalVisible(true);
      }
    }
  };







  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  


  if (loading) {
    return null;
  }

  // Path'te 2 veya daha fazla segment varsa VE entry bir dosya ise FileDetail render et
  const pathSegments = fullPath.split('/').filter(p => p && p.trim() !== '');
  const isFile = entry && (entry.entryType === 'File' || entry.entryType === 2 || String(entry.entryType) === 'File');

  // entry tipi File ise segment sayısından bağımsız olarak FileDetail göster
  if (isFile) {
    return (
      <HeaderlessLayout title={entry?.name || pathSegments[1] || 'File'} icon={<ProjectOutlined />} forceInitialCollapse={true}>
        
        <FileDetail breadcrumbPathParts={pathParts} bclocation={location} fileDetail={entry} onBack={handleGoBack} />
      </HeaderlessLayout>
    );
  }

  if (!entry) {
    return (
      <MainLayout title={t('projectDetail.projectNotFound')} icon={<ProjectOutlined />}>
        <div>{t('projectDetail.projectNotFound')}</div>
        <Button onClick={() => navigate(getProjectsBasePath())} style={{ marginTop: 16 }}>
          {t('breadcrumb.projects')}
        </Button>
      </MainLayout>
    );
  }

  return (
    <MainLayout title={entry.name} icon={<ProjectOutlined />}>
      {user?.role !== 'BrandAccountViewer' && (
        <NewEntrySection
          userRole={user?.role}
          isPopoverOpen={isEntryPopoverOpen}
          onPopoverOpenChange={setIsEntryPopoverOpen}
          onCreateFolder={() => setIsFolderModalOpen(true)}
          onCreateFile={() => setIsFileModalOpen(true)}
        />
      )}

        <div className={styles.projectGrid}>
        {Array.isArray(entry.children) && entry.children.length > 0 ? (
          entry.children.map((item: any) => (
            <ProjectItemCard
              key={item.id}
              item={item}
              isSelected={selectedItems.has(item.id)}
              onSelectChange={(checked: boolean) => handleItemSelect(item.id, checked)}
              onNavigate={handleNavigation}
              formatDate={formatDate}
              selectable={user?.role !== 'BrandAccountViewer'}
            />
          ))
        ) : (
          <NoDataComponent message="No content in this folder" />
        )}
        </div>

      {/* FooterActions - Sadece seçim yapıldığında göster */}
      {selectedItems.size > 0 && (
        <FooterActions
          onDelete={handleBulkDelete}
          onDownload={handleBulkDownload}
          onCopyUrl={handleBulkCopyUrl}
          onApprovalRequest={() => setIsApprovalModalVisible(true)} // Approval modal'ı aç
          onEdit={handleEditClick} // Edit fonksiyonu ekle
          fileDetail={null} // ProjectDetail'da fileDetail yok
          selectedItems={selectedItems}
          selectedItemsDetails={
            entry?.children && Array.isArray(entry.children)
              ? entry.children
                  .filter((item: any) => selectedItems.has(item.id))
                  .map((item: any) => ({
                    id: item.id,
                    name: item.name,
                    entryType: item.entryType,
                    status: item.status,
                    share: item.share || [] // Share bilgisini de ekle
                  }))
              : []
          }
          currentEntry={(() => {
            // Seçili öğe varsa onu, yoksa ana projeyi geç
            if (selectedItems.size > 0) {
              const selectedItem = entry?.children?.find((item: any) => selectedItems.has(item.id));
              return selectedItem || entry;
            }
            return entry;
          })()}
          hideDownload={true} // ProjectDetail'da download butonunu gizle
          style={{
            transform: 'translateY(0)',
            transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out',
            opacity: 1
          }}
        />
      )}

      {/* Footer Actions için safe alan */}
      <div className={styles.footerSafeArea} />

      <AddNewFolderModal 
        isOpen={isFolderModalOpen} 
        onClose={() => setIsFolderModalOpen(false)}
        parentId={entry?.entryType === 'Project' ? undefined : entry?.id} // Proje ise undefined, Folder ise entry.id
        projectId={projectId}

        onSuccess={handleRefresh}
      />
      <AddNewFileModal 
        isOpen={isFileModalOpen} 
        onClose={() => setIsFileModalOpen(false)}
        parentId={entry?.entryType === 'Project' ? undefined : entry?.id} // Proje ise undefined, Folder ise entry.id
        projectId={projectId}
        onSuccess={(newVersionData) => handleRefresh()}
      />
      
      {/* Edit Modals */}
      <AddNewFileModal
        isOpen={isEditFileModalVisible}
        onClose={() => setIsEditFileModalVisible(false)}
        onSuccess={(newVersionData) => {
          setIsEditFileModalVisible(false);
          handleRefresh();
        }}
        isFromFileDetail={false}
        editMode={true}
        editData={(() => {
          const selectedItem = entry?.children?.find((item: any) => selectedItems.has(item.id));
          return selectedItem ? {
            ...selectedItem,
            share: selectedItem.share || []
          } : {};
        })()}
      />

      <AddNewFolderModal
        isOpen={isEditFolderModalVisible}
        onClose={() => setIsEditFolderModalVisible(false)}
        onSuccess={() => {
          setIsEditFolderModalVisible(false);
          handleRefresh();
        }}
        editMode={true}
        editData={(() => {
          const selectedItem = entry?.children?.find((item: any) => selectedItems.has(item.id));
          return selectedItem ? {
            ...selectedItem,
            share: selectedItem.share || []
          } : {};
        })()}
      />

      <AddNewProjectModal
        isOpen={isEditProjectModalVisible}
        onClose={() => setIsEditProjectModalVisible(false)}
        onSuccess={() => {
          setIsEditProjectModalVisible(false);
          handleRefresh();
        }}
        editMode={true}
        editData={(() => {
          const selectedItem = entry?.children?.find((item: any) => selectedItems.has(item.id));
          return selectedItem ? {
            ...selectedItem,
            share: selectedItem.share || []
          } : {};
        })()}
      />
      
      {/* Approval Modal */}
      <ApprovalModal
        visible={isApprovalModalVisible}
        onCancel={handleApprovalCancel}
        onConfirm={handleApprovalConfirm}
        loading={approvalLoading}
        hasSubentries={
          selectedItems.size > 0 
            ? // Seçili itemlar varsa onların alt öğelerini kontrol et
              entry?.children?.some((child: any) => 
                selectedItems.has(child.id) && (
                  child.entryType === 'Project' || 
                  child.entryType === 'Folder' || 
                  child.entryType === 'File'
                )
              ) || false
            : // Seçili item yoksa mevcut entry'nin children'ını kontrol et
              (entry?.children && entry.children.length > 0) || false
        }
      />
    </MainLayout>
  );
};

export default ProjectDetail; 